import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useGoBack } from '../hooks/useGoBack'
import Header from '../components/Header'
import { useStore, type BankTransferInstructions } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import { api } from '../services/api'
import { demoUiEnabled } from '../lib/appEnv'

/**
 * #1188 Phase 2: 日本の銀行振込チャージ画面。
 * Stripe customer_balance で発行された Zengin 口座情報を表示し、
 * 客が銀行アプリから振込 → charge webhook 経由で残高が非同期反映される。
 *
 * revolut ルート (`ChargeRevolut.tsx`) と backend 経路は同一で、
 * marketing コピー (見出し・注意文) だけ差別化した薄い wrapper。
 * 実処理はどちらもこの体で共有する。
 */
export default function ChargeBank({
  variant = 'bank',
}: {
  variant?: 'bank' | 'revolut'
}) {
  const navigate = useNavigate()
  // #1361 戻るは履歴を1つ戻す（行き先を push すると履歴が積み上がる）。
  // fallback は直リンク着地時のみ使われる。
  const goBackToCharge = useGoBack('/charge')
  const goBackToMethod = useGoBack('/charge/method')
  const location = useLocation()
  const { t } = useI18n()
  const createBankTransferIntent = useStore((s) => s.createBankTransferIntent)

  const state = (location.state ?? {}) as { amount?: number }
  const [loading, setLoading] = useState(true)
  const [instructions, setInstructions] = useState<BankTransferInstructions | null>(null)
  const [error, setError] = useState<string | null>(null)
  const chargeIdRef = useRef<string | null>(null)
  // #1188 Phase 2: React StrictMode の useEffect double-fire 防止。
  // useEffect の cancelled フラグは fetch 結果を破棄するだけで、既に投げた
  // API call を止められない。Stripe PaymentIntent は confirm=true で発行するため、
  // 二重発火すると orphan PI が Stripe に残り、client_secret が返らない側の PI が
  // FIFO で cash balance を先取りしてしまう。useRef で「作成中/作成済み」を跨ぎ
  // guard する (React StrictMode は同一 fiber の同一 ref を共有する)。
  const creatingRef = useRef(false)

  useEffect(() => {
    if (!state.amount) return
    if (creatingRef.current) return
    creatingRef.current = true
    // NOTE: cancelled flag は使わない。
    // React StrictMode の useEffect double-fire では 1 回目の cleanup で
    // cancelled=true になるが、2 回目は creatingRef guard で早期 return する
    // ため、1 回目の async が完了しても状態更新できず loading が永久に true
    // に張り付く。ref guard で「呼ばれるのは 1 回だけ」を保証してるので、
    // 状態更新は無条件で走らせる (real unmount 後の setState は React が
    // 警告するだけで crash しない)。
    ;(async () => {
      try {
        // #1764 Revolut 画面からの振込は履歴で見分けられるように channel を付ける
        const res = await createBankTransferIntent(state.amount!, variant === 'revolut' ? 'revolut' : undefined)
        setInstructions(res)
        chargeIdRef.current = res.charge_id
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        setLoading(false)
      }
    })()
  }, [state.amount, variant, createBankTransferIntent])

  // #1188 Phase 2: charge intent の status を数秒間隔で poll。
  // GET /charge/intent が Stripe の PaymentIntent を照合して 'pending' → 'executed'
  // にしたら (#1764)、残高を refetch して success 画面に遷移する。
  useEffect(() => {
    if (!instructions || !instructions.charge_id) return
    let cancelled = false
    const chargeId = instructions.charge_id

    const poll = async () => {
      if (cancelled) return
      try {
        const status = await api.getChargeIntentStatus(chargeId)
        if (cancelled) return
        // #1765 別の振込画面を開くと古い intent は取り下げられる (入金は一番古い open な
        // PaymentIntent に付くため、開いているものを 1 つにしている)。この画面は無効。
        if (status?.status === 'canceled') {
          cancelled = true
          setInstructions(null)
          setError(t('chargeBank.superseded'))
          return
        }
        if (status?.status === 'executed') {
          // 残高更新: store の refreshSession を叩いて Firestore の最新値を取得
          const { userId, restoreSession } = useStore.getState()
          if (userId) {
            try { await restoreSession() } catch { /* best-effort */ }
          }
          // charge success 画面が読める形で state を設定
          useStore.setState({
            lastChargeAmount: status.amount_jpy,
            lastChargeTxHash: null,
            lastChargeExplorerLink: null,
            lastTxTimestamp: Date.now(),
          })
          navigate('/charge/success')
        }
      } catch {
        // ignore transient errors (404 during creation race, network hiccup 等)
      }
    }

    // 初回即実行 + 最初の 1 分は 3 秒間隔 (デモのシミュレート直後に拾う)、以降は 15 秒。
    // poll のたびに backend が Stripe を照会するので、振込画面を開きっぱなしの客で
    // Stripe を叩き続けない (実際の振込は数分〜1 営業日かかる)。
    const startedAt = Date.now()
    let timer: number | undefined
    const schedule = () => {
      if (cancelled) return
      const delay = Date.now() - startedAt < 60_000 ? 3000 : 15_000
      timer = window.setTimeout(async () => {
        await poll()
        schedule()
      }, delay)
    }
    poll()
    schedule()
    return () => {
      cancelled = true
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [instructions, navigate, t])

  if (!state.amount) {
    return (
      <div className="flex min-h-screen flex-col bg-warm-white safe-top safe-bottom">
        <Header
          title={t(variant === 'revolut' ? 'chargeRevolut.title' : 'chargeBank.title')}
          onBack={goBackToCharge}
        />
        <div className="p-6 text-center text-sm text-text-secondary">
          {t('chargeMethod.selectAmountFirst')}
        </div>
      </div>
    )
  }

  const zengin = instructions?.funding_instructions?.financial_addresses?.[0]?.zengin

  return (
    <div className="flex min-h-screen flex-col bg-warm-white safe-top safe-bottom">
      <Header
        title={t(variant === 'revolut' ? 'chargeRevolut.title' : 'chargeBank.title')}
        onBack={goBackToMethod}
      />

      <div className="flex flex-col items-center py-4">
        <span className="font-body text-xs text-text-secondary">
          {t('chargeBank.transferAmount')}
        </span>
        <span className="mt-1 font-display text-[24px] font-bold text-gold">
          &yen; {instructions?.total_jpy?.toLocaleString() ?? state.amount.toLocaleString()}
        </span>
        {instructions && (
          <span className="mt-0.5 font-body text-xs text-text-secondary">
            {t('chargeBank.feeBreakdown', {
              amount: instructions.amount_jpy.toLocaleString(),
              fee: instructions.fee_jpy.toLocaleString(),
            })}
          </span>
        )}
      </div>

      <div className="mx-4 border-t border-gray-200" />

      {loading && (
        <div className="p-6 text-center font-body text-sm text-text-secondary">
          {t('chargeBank.preparingAccount')}
        </div>
      )}

      {error && (
        <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="font-body text-sm text-red-600">
            {t('chargeBank.errorLoad')}
          </p>
          <p className="font-body text-xs text-red-500 mt-1 break-all">{error}</p>
        </div>
      )}

      {/* #1188 Phase 2: demo mode 限定シミュレートボタン。
         Stripe CLI + fund_cash_balance を叩ける人しか E2E 検証できない属人化を解消
         (backend も APP_ENV=prod で 404 を返す safety net あり)。 */}
      {demoUiEnabled() && instructions?.charge_id && (
        <div className="mx-4 mt-4 rounded-lg border border-dashed border-navy/40 bg-navy/5 p-3">
          <p className="font-body text-[11px] font-semibold uppercase tracking-wide text-navy">
            {t('chargeBank.demoBoxTitle')}
          </p>
          <p className="mt-1 font-body text-xs text-text-secondary leading-5">
            {t('chargeBank.demoBoxBody')}
          </p>
          <button
            type="button"
            onClick={async () => {
              try {
                await api.simulateBankTransferComplete(instructions.charge_id)
                // 以降は既存の polling が 3 秒以内に status=executed を拾って
                // /charge/success に自動遷移する。ここでは手動 navigate しない。
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e))
              }
            }}
            className="tap-effect mt-2 h-10 w-full rounded-lg bg-navy font-display text-xs font-semibold text-white"
          >
            {t('chargeBank.demoSimulateCta')}
          </button>
        </div>
      )}

      {zengin && (
        <>
          <h2 className="px-4 pt-5 pb-3 font-display text-base font-semibold text-text-primary">
            {t(variant === 'revolut' ? 'chargeRevolut.pitch' : 'chargeBank.pitch')}
          </h2>

          <div className="mx-4 rounded-lg border border-gray-200 bg-white p-4">
            <dl className="divide-y divide-gray-100 text-sm">
              <Row label={t('chargeBank.bankName')} value={`${zengin.bank_name} (${zengin.bank_code})`} />
              <Row label={t('chargeBank.branchName')} value={`${zengin.branch_name} (${zengin.branch_code})`} />
              <Row
                label={t('chargeBank.accountType')}
                value={
                  zengin.account_type === 'futsu'
                    ? t('chargeBank.accountTypeFutsu')
                    : t('chargeBank.accountTypeToza')
                }
              />
              <Row label={t('chargeBank.accountNumber')} value={zengin.account_number} copyable />
              <Row label={t('chargeBank.accountHolder')} value={zengin.account_holder_name} copyable />
            </dl>
          </div>

          <div className="mx-4 mt-4 rounded-lg bg-yellow-50 border border-yellow-200 p-3">
            <p className="font-body text-xs text-yellow-800 leading-5">
              {t('chargeBank.warning')}
            </p>
          </div>

          {instructions?.funding_instructions?.hosted_instructions_url && (
            <div className="px-4 mt-3">
              <a
                href={instructions.funding_instructions.hosted_instructions_url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-body text-xs text-yokoso-red underline"
              >
                {t('chargeBank.viewStripeInstructions')}
              </a>
            </div>
          )}
        </>
      )}

      <div className="flex-1" />

      <div className="px-4 pb-6 pt-4">
        <button
          onClick={() => navigate('/home')}
          className="tap-effect h-14 w-full rounded-xl bg-yokoso-red font-display text-base font-semibold text-white shadow-button"
        >
          {t('chargeBank.doneCta')}
        </button>
      </div>
    </div>
  )
}

function Row({
  label,
  value,
  copyable,
}: {
  label: string
  value: string
  copyable?: boolean
}) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    if (typeof navigator === 'undefined') return
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1200)
    })
  }

  return (
    <div className="flex items-center justify-between py-2">
      <dt className="font-body text-xs text-text-secondary">{label}</dt>
      <dd className="flex items-center gap-2 font-body text-sm font-medium text-text-primary">
        <span className="break-all text-right">{value}</span>
        {copyable && (
          <button
            onClick={handleCopy}
            className="rounded border border-gray-300 px-2 py-0.5 text-[10px] text-text-secondary"
            type="button"
          >
            {copied ? '✓' : 'Copy'}
          </button>
        )}
      </dd>
    </div>
  )
}
